import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AUTOMATION_PATHS, JOURNEY_BACK_URL_KEY } from '@/modules/AutomationModuleNew/constants';
import { useNavigateAndBack } from '@/modules/AutomationModuleNew/hooks/useNavigateAndBack';
import {
  atomActionInfoEmail,
  atomProcessingAction,
} from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import { haveMessageAtom } from '@/modules/CampaignsModule/atoms/actionsAtom';
import { CAMPAIGNS_PATH } from '@/modules/CampaignsModule/constants';
import { useActionEmailData } from '@/modules/CampaignsModule/hooks/useActionEmailData';
import {
  atomIsLoadingRuleData,
  atomRuleDataRaw,
  atomToggleActivateRuleModal,
} from '@/modules/RulesModule/atoms/rules';
import { useRuleData } from '@/modules/RulesModule/hooks';
import { useResetRulesState } from '@/modules/RulesModule/hooks/useResetRulesState';
import {
  IRulesEmailFormInfo,
  IRulesEmailInfoPayload,
  RuleTypes,
} from '@/modules/RulesModule/interfaces';

/* cspell:disable*/
export const useRulesEmailFormSubmit = () => {
  const navigate = useNavigate();
  const { campaignId, actionId, actionType } = useParams();
  const { pathname } = useLocation();

  const actionInfoEmail = useAtomValue(atomActionInfoEmail);
  const setHaveMessage = useSetAtom(haveMessageAtom);
  const rulesDataRaw = useAtomValue(atomRuleDataRaw);
  const isLoadingRuleData = useAtomValue(atomIsLoadingRuleData);
  const setProcessingAction = useSetAtom(atomProcessingAction);
  const setToggleActivateRuleModal = useSetAtom(atomToggleActivateRuleModal);
  const { hasBackUrl, backUrl } = useNavigateAndBack(JOURNEY_BACK_URL_KEY);
  const { activateRule } = useRuleData(Number(actionId));

  const { saveInfoAction } = useActionEmailData(Number(actionId));
  const { refetchData } = useResetRulesState();

  const rulePath = useMemo(() => {
    const isJourneysPath = pathname.includes(
      `${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}`
    );
    const journeysPath = hasBackUrl ? backUrl : -1;
    const campaignsPath = `${CAMPAIGNS_PATH}/${String(campaignId)}/${String(actionType)}/${String(
      actionId
    )}`;

    return isJourneysPath ? journeysPath : campaignsPath;
  }, [pathname, hasBackUrl, backUrl, campaignId, actionType, actionId]);

  const handleSubmitData = useCallback(
    async (data: IRulesEmailFormInfo, activate: boolean = false) => {
      if (!actionInfoEmail?.content?.message) {
        setHaveMessage(true);
        return;
      }

      setProcessingAction(true);

      const { sender, subject, firstLine } = data;

      const payload: IRulesEmailInfoPayload = {
        senderId: Number(sender.id),
        subject: subject,
        firstLine: firstLine,
      };

      await saveInfoAction(payload);
      if (activate) await activateRule();
      refetchData();
      setProcessingAction(false);

      if (rulesDataRaw?.desencadenante === RuleTypes.FLOW_TEMPLATE && !isLoadingRuleData) {
        setToggleActivateRuleModal(true);
        return;
      }

      if (rulePath !== -1) {
        navigate(rulePath || '');
      } else {
        navigate(-1);
      }
    },
    [
      actionInfoEmail?.content?.message,
      activateRule,
      isLoadingRuleData,
      navigate,
      refetchData,
      rulePath,
      rulesDataRaw?.desencadenante,
      saveInfoAction,
      setHaveMessage,
      setProcessingAction,
      setToggleActivateRuleModal,
    ]
  );

  return {
    handleSubmitData,
  };
};
/* cspell:enable*/
